// 下游客户热力图（/downstream-heat）
// 由康巴圣德经营数据页（/sell）中间地图上方「下游客户热力图」标签进入
// 展示：以康巴圣德为枢纽、31 家下游/关联企业的「酪蛋白」公开信息热力网络
// 渲染采用原生 Canvas 自绘（ECharts graph 局部 setOption 存在清屏不重绘的渲染缺陷，已弃用）
// 热力口径见 src/data/caseinHeat.js：正向使用频次 × 应用强度（负面表述按弱关联计）
// 本页按 2221 大屏基准 7680×2160 排布（2223 副本为 3840×2160，比例不同，故重排）
<template>
  <div class="dh-page" :style="rootVars">
    <div class="dh-stage" ref="stage">
          <!-- 闪烁星空斑点层（纯装饰，pointer-events 不挡交互） -->
          <div class="dh-stars" aria-hidden="true">
            <i v-for="(s, i) in stars" :key="i" :class="s.cls" :style="s.style"></i>
          </div>
          <!-- 页头 -->
          <header class="dh-header">
            <div class="dh-header-main">
              <h1 class="dh-title"><i class="dh-flame"></i>{{ $C('downstreamHeat','pageTitle','下游客户热力图') }}</h1>
              <div class="dh-chips">
                <span class="dh-chip dh-chip-key">{{ $C('downstreamHeat','chipKeyword','关键词：酪蛋白') }}</span>
                <span class="dh-chip">{{ heatList.length }} {{ $C('downstreamHeat','chipCountSuf','家下游/关联企业') }}</span>
                <span class="dh-chip dh-chip-list">{{ $C('downstreamHeat','chipListed','上市公司') }} {{ listedCount }} {{ $C('downstreamHeat','chipListedSuf','家') }}</span>
                <span class="dh-chip">{{ $C('downstreamHeat','chipHeat','热力 = 「酪蛋白」公开信息正向使用频次 × 应用强度') }}</span>
              </div>
            </div>
            <button class="dh-back" type="button" @click="goBack">
              <span class="dh-back-arrow">←</span> {{ $C('downstreamHeat','backText','返回经营数据') }}
            </button>
          </header>

          <!-- 主体：左热力榜 + 右游走网络图 -->
          <div class="dh-body">
            <aside class="dh-board">
              <div class="dh-board-head"><i class="dh-board-pin"></i>{{ $C('downstreamHeat','boardHead','酪蛋白热力 TOP 10') }}</div>
              <ul class="dh-rank">
                <li v-for="(c, i) in top10" :key="c.id" :class="['dh-row', 'rank-' + (i + 1)]" @click="goCompany(c.id)">
                  <span class="dh-rank-no">{{ i + 1 }}</span>
                  <div class="dh-row-main">
                    <div class="dh-row-line1">
                      <span class="dh-row-name">{{ c.short }}</span>
                      <span class="dh-row-listed" v-if="c.isListed">{{ c.stock.market }} {{ c.stock.code }}</span>
                      <span class="dh-row-prov">{{ c.province }}</span>
                      <span class="dh-row-heat">{{ c.heat }}</span>
                    </div>
                    <div class="dh-bar"><i class="dh-bar-fill" :style="{ width: barWidth(c.heat) }"></i></div>
                  </div>
                </li>
              </ul>
            </aside>
            <div class="dh-chart-wrap">
              <canvas class="dh-canvas" ref="heatCanvas"></canvas>
              <div class="dh-tooltip" v-show="tip.show" :style="tipStyle" v-html="tip.html"></div>
              <div class="dh-chart-tip">{{ $C('downstreamHeat','chartTip','节点大小与颜色 = 热力值 · 节点持续缓慢游走中 · 点击企业节点查看详情') }}</div>
            </div>
          </div>

          <!-- 页脚 -->
          <footer class="dh-footer">
            <span class="dh-footer-logo">{{ $C('downstreamHeat','footerLogo','康巴圣德') }}</span>
            <span class="dh-footer-text">{{ $C('downstreamHeat','footerText','数据来源：企业公开工商 / 专利 / 官网 / 年报 / 环评 / 行业公开资讯中「酪蛋白」字样正向使用频次推演，仅供市场调研与数字看板展示') }}</span>
            </footer>
        </div>
  </div>
</template>

<script>
import { getCompanyById, countListedCompanies } from '@/data/companyNetwork'
import { caseinHeat } from '@/data/caseinHeat'
import { mediaUrl } from '@/cms/config'

// 用户指定着橙色的 16 家企业；其余下游企业着蓝色，枢纽「康巴圣德」保持青色不变
const ORANGE_TEAM = [
  'fujian-bangling', 'jiangsu-jiahe', 'jiangsu-taihe', 'guangdong-maiji',
  'sichuan-baoshun', 'jiangsu-lianfeng', 'guangdong-yuanjian', 'hebei-haoshidai',
  'henan-jinyumi', 'henan-boxin', 'guangdong-duohe', 'zhejiang-ganwei',
  'jiangxi-hengding', 'jiangxi-changhui', 'shandong-chunhua', 'zhejiang-boduo',
]

// 用户指定放大显示的企业（圆与字号略加大，便于辨识）
const BIGGER_TEAM = [
  'zhejiang-ganwei', 'guangdong-maiji', 'shandong-chunhua',
  'jiangsu-jiahe', 'hebei-haoshidai', 'jiangsu-taihe',
]

export default {
  name: 'downstreamHeat',
  data() {
    // 星空斑点：伪随机（固定种子）生成一次，布局确定不跳动
    const stars = []
    let seed = 20260918
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280 }
    // 590 颗常规星（2~8px）
    for (let i = 0; i < 590; i++) {
      const size = 2 + rnd() * 6
      const r = rnd()
      stars.push({
        style: {
          left: (rnd() * 100).toFixed(2) + '%',
          top: (rnd() * 100).toFixed(2) + '%',
          width: size.toFixed(1) + 'px',
          height: size.toFixed(1) + 'px',
          animationDuration: (2.2 + rnd() * 4.5).toFixed(2) + 's',
          animationDelay: (-rnd() * 7).toFixed(2) + 's',
        },
        cls: r < 0.22 ? 's-cyan' : (r < 0.36 ? 's-gold' : 's-white'),
      })
    }
    // 100 颗大星（10~18px，更醒目）
    for (let i = 0; i < 100; i++) {
      const size = 10 + rnd() * 8
      const r = rnd()
      stars.push({
        style: {
          left: (rnd() * 100).toFixed(2) + '%',
          top: (rnd() * 100).toFixed(2) + '%',
          width: size.toFixed(1) + 'px',
          height: size.toFixed(1) + 'px',
          animationDuration: (2.6 + rnd() * 5).toFixed(2) + 's',
          animationDelay: (-rnd() * 8).toFixed(2) + 's',
        },
        cls: r < 0.22 ? 's-cyan' : (r < 0.36 ? 's-gold' : 's-white') + ' s-big',
      })
    }
    return {
      stars,
      tip: { show: false, x: 0, y: 0, html: '' },
    }
  },
  computed: {
    heatSource() {
      return this.$CList('downstreamHeat', 'caseinHeat', caseinHeat)
    },
    heatList() {
      return this.heatSource
        .map(h => {
          const c = getCompanyById(h.id)
          return c ? Object.assign({}, h, {
            short: c.short, name: c.name, province: c.province, category: c.category,
            isListed: c.isListed, stock: c.stock,
          }) : null
        })
        .filter(Boolean)
        .sort((a, b) => b.heat - a.heat)
    },
    top10() {
      return this.heatList.slice(0, 10)
    },
    listedCount() {
      return countListedCompanies()
    },
    tipStyle() {
      return {
        left: this.tip.x + 'px',
        top: this.tip.y + 'px',
      }
    },
    rootVars() {
      const bg = this.$C('downstreamHeat', 'pageBg', '')
      const vars = {
        '--text-main': '#eaf3ff',
        '--text-sub': '#9fc0e8',
        '--accent': '#31FFFF',
        '--accent2': '#FFC857',
        '--card-bg': 'rgba(13,42,84,0.55)',
        '--card-border': 'rgba(79,163,255,0.32)',
        '--radius': '16px',
      }
      vars['--page-bg'] = bg
        ? `url(${mediaUrl(bg)}) center/cover no-repeat #04122e`
        : 'linear-gradient(160deg,#04122e 0%,#0a2248 55%,#061634 100%)'
      return vars
    },
  },
  mounted() {
    this._raf = null
    this._cycleTimer = null
    this.$nextTick(() => this.init())
    window.addEventListener('resize', this.onResize)
  },
  activated() {
    // keep-alive 重新进入：重新适配 canvas 并恢复动画（整体缩放由外壳 main-layout 统一处理）
    this.$nextTick(() => {
      this.fitCanvas()
      this.startWander()
      this.startCycle()
    })
  },
  deactivated() {
    this.stopCycle()
    this.stopWander()
    this.hideTip()
  },
  beforeDestroy() {
    this.stopCycle()
    this.stopWander()
    window.removeEventListener('resize', this.onResize)
    if (this._ro) this._ro.disconnect()
    this.detachCanvasEvents()
  },
  methods: {
    goBack() {
      // 返回康巴圣德经营数据页
      this.$router.replace('/sell')
    },
    goCompany(id) {
      this.$router.push('/company-detail/' + id)
    },
    barWidth(heat) {
      return Math.round((heat / 100) * 100) + '%'
    },
    onResize() {
      this.fitCanvas()
    },
    // ============ 原生 Canvas 自绘网络 ============
    init() {
      this.fitCanvas()
      this.attachCanvasEvents()
      this.startWander()
      this.startCycle()
    },
    fitCanvas() {
      const el = this.$refs.heatCanvas
      if (!el) return
      const cw = el.clientWidth || 5200
      const ch = el.clientHeight || 1500
      const dpr = window.devicePixelRatio || 1
      el.width = Math.round(cw * dpr)
      el.height = Math.round(ch * dpr)
      this._ctx = el.getContext('2d')
      this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      this._cw = cw
      this._ch = ch
      this.buildNodes()
    },
    nodeColor(heat, id) {
      if (id && ORANGE_TEAM.indexOf(id) > -1) return '#FF8A3D'   // 指定 16 家：橙色
      return '#31E58B'                                           // 其余下游企业：绿色
    },
    // 构建节点：枢纽居中固定 + 费马螺旋（phyllotaxis）紧凑铺布
    // 热力越高越靠近中心、越醒目；整体从 hub 外缘向外自然填充，消除中心大片空白
    // 注意：本页画布宽约 5200（7680 基准），固定半径/字号按 2223 的 ~2 倍放大以保持视觉一致
    buildNodes() {
      const cw = this._cw || 5200
      const ch = this._ch || 1500
      const cx = cw / 2
      const cy = ch / 2
      const N = this.heatList.length
      // 椭圆半轴：贴合画布（留边距），横向可略宽于纵向，整体填满不留大空白
      const hubR = 130
      // 椭圆铺布：画布横向远宽于纵向，故按椭圆半轴分布——横向大幅向两边散开，
      // 纵向受画布高度限制（需留出节点半径 + 下方标签的垂直空间）
      const maxX = cw / 2 - 200
      const maxY = Math.min(ch / 2 - 230, maxX)
      const hubEdgeX = hubR + 360          // 第一圈距枢纽的横向净空
      const hubEdgeY = hubR + 210          // 第一圈距枢纽的纵向净空
      // 各节点独立低频小幅游走参数（慢慢平滑漂移，幅度小以配合碰撞约束）
      const mkWander = (amp) => ({
        amp,
        s1: 0.00010 + Math.random() * 0.00009,
        s2: 0.00017 + Math.random() * 0.00012,
        s3: 0.00012 + Math.random() * 0.00010,
        s4: 0.00019 + Math.random() * 0.00013,
        p1: Math.random() * 6.28, p2: Math.random() * 6.28,
        p3: Math.random() * 6.28, p4: Math.random() * 6.28,
      })
      const nodes = [{
        id: 'hub',
        name: this.$C('downstreamHeat', 'hubName', '康巴圣德'),
        province: '四川',
        heat: 100,
        mentions: 0,
        note: this.$C('downstreamHeat', 'hubNote', '酪蛋白酸钠生产商 · 热力枢纽'),
        r: hubR,
        color: '#00E5FF',
        labelColor: '#fff',
        labelSize: 64,
        x: cx, y: cy, ax: cx, ay: cy,
        w: mkWander(0), // 枢纽固定居中，作为稳定参考中心
      }]
      this.heatList.forEach((h, i) => {
        // 费马螺旋（黄金角）+ 椭圆半轴：半径随序号平方根增长 → 从枢纽外缘向外自然铺满整个椭圆
        // 横向半轴远大于纵向半轴，节点向左右两边充分散开，不再挤成中间一团
        const theta = i * 2.399963
        const rr = N > 1 ? i / (N - 1) : 0
        const f = Math.sqrt(rr)
        const radX = hubEdgeX + (maxX - hubEdgeX) * f
        const radY = hubEdgeY + (maxY - hubEdgeY) * f
        const ax = cx + Math.cos(theta) * radX
        const ay = cy + Math.sin(theta) * radY
        const bigger = BIGGER_TEAM.indexOf(h.id) > -1
        const baseR = (50 + h.heat * 1.3) / 2              // 适度收小圆径，为间距让位
        const baseLabel = h.heat >= 80 ? 56 : (h.heat >= 60 ? 50 : 44)
        nodes.push({
          id: h.id,
          name: h.short,
          province: h.province,
          heat: h.heat,
          mentions: h.mentions,
          note: h.note,
          r: bigger ? Math.max(baseR, 62) : baseR,          // 放大组半径下限 62
          color: this.nodeColor(h.heat, h.id),
          labelColor: ORANGE_TEAM.indexOf(h.id) > -1 ? '#FFF0DC' : '#DFF7E9',
          labelSize: bigger ? Math.max(baseLabel, 52) : baseLabel, // 放大组字号下限 52
          x: ax, y: ay, ax, ay,
          w: mkWander(28 + Math.random() * 22), // 游走幅度同步收小，降低碰撞压力
        })
      })
      this._nodes = nodes
    },
    // 节点各自缓慢游走 + 软碰撞分离：rAF 逐帧更新坐标并重绘
    // 连续平滑漂移（非跳动式）；碰撞分离保证游动过程中节点互不重叠，hub 固定不被推挤
    startWander() {
      this.stopWander()
      if (!this._nodes || !this._ctx) return
      const step = () => {
        const t = performance.now()
        const nodes = this._nodes
        const cw = this._cw
        const ch = this._ch
        // 1) 李萨如小幅游走（围绕各自锚点平滑漂移）
        nodes.forEach(n => {
          const w = n.w
          n.x = n.ax + w.amp * Math.sin(t * w.s1 + w.p1) + w.amp * 0.45 * Math.sin(t * w.s2 + w.p2)
          n.y = n.ay + w.amp * Math.cos(t * w.s3 + w.p3) + w.amp * 0.45 * Math.sin(t * w.s4 + w.p4)
        })
        // 2) 软碰撞分离：任意两节点过近则沿连线方向轻柔推开，避免重叠（hub 固定）
        // 区分两档间隙：公司↔公司大幅拉开；公司↔枢纽保持适度留白。
        // 每帧迭代两轮，确保游走过程中重叠也能被完全化解
        const gapNormal = 64   // 公司 ↔ 公司：大幅拉开彼此距离，圆互不重叠
        const gapHub = 40      // 公司 ↔ 枢纽：保持枢纽留白适中
        for (let pass = 0; pass < 2; pass++) {
          for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
              const a = nodes[i]
              const b = nodes[j]
              let dx = a.x - b.x
              let dy = a.y - b.y
              let dist = Math.hypot(dx, dy)
              const isHubPair = a.id === 'hub' || b.id === 'hub'
              const gap = isHubPair ? gapHub : gapNormal
              const minD = a.r + b.r + gap
              if (dist < minD) {
                if (dist < 0.01) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; dist = Math.hypot(dx, dy) || 1 }
                const nx = dx / dist
                const ny = dy / dist
                const overlap = minD - dist
                const aFix = a.id === 'hub' ? 0 : (b.id === 'hub' ? overlap : overlap * 0.5)
                const bFix = b.id === 'hub' ? 0 : (a.id === 'hub' ? overlap : overlap * 0.5)
                a.x += nx * aFix; a.y += ny * aFix
                b.x -= nx * bFix; b.y -= ny * bFix
              }
            }
          }
        }
        // 3) 边界约束：确保节点留在画布内（纵向额外预留节点下方标签的高度，防止文字出界）
        nodes.forEach(n => {
          if (n.id === 'hub') return
          n.x = Math.min(Math.max(n.x, n.r + 20), cw - n.r - 20)
          n.y = Math.min(Math.max(n.y, n.r + 20), ch - n.r - n.labelSize - 34)
        })
        this.drawFrame()
        this._raf = requestAnimationFrame(step)
      }
      this._raf = requestAnimationFrame(step)
    },
    stopWander() {
      if (this._raf) {
        cancelAnimationFrame(this._raf)
        this._raf = null
      }
    },
    drawFrame() {
      const ctx = this._ctx
      if (!ctx) return
      const cw = this._cw
      const ch = this._ch
      ctx.clearRect(0, 0, cw, ch)
      this._nodes.forEach(n => {
        const hl = this._highlightName === n.name
        // 光晕
        ctx.shadowBlur = hl ? 92 : (n.id === 'hub' ? 68 : (n.heat >= 60 ? 48 : 16))
        ctx.shadowColor = hl ? '#ffffff' : n.color
        // 圆体
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2)
        ctx.fillStyle = n.color
        ctx.fill()
        // 描边
        ctx.shadowBlur = 0
        ctx.lineWidth = n.id === 'hub' ? 6 : 4
        ctx.strokeStyle = 'rgba(255,255,255,0.75)'
        ctx.stroke()
        // 标签
        ctx.font = (hl ? 'bold ' : '') + (hl ? Math.max(n.labelSize, 56) : n.labelSize) + 'px "Microsoft YaHei", sans-serif'
        ctx.fillStyle = hl ? '#fff' : n.labelColor
        ctx.textAlign = 'center'
        ctx.textBaseline = 'top'
        ctx.fillText(n.name, n.x, n.y + n.r + 20)
      })
    },
    // 动态效果：每 2.8s 轮流高亮热力 TOP5 节点（发光扫描）
    startCycle() {
      this.stopCycle()
      const order = [this.$C('downstreamHeat', 'hubName', '康巴圣德')].concat(this.top10.slice(0, 5).map(c => c.short))
      let idx = 0
      this._cycleTimer = setInterval(() => {
        this._highlightName = order[idx % order.length]
        idx = (idx + 1) % order.length
      }, 2800)
    },
    stopCycle() {
      if (this._cycleTimer) {
        clearInterval(this._cycleTimer)
        this._cycleTimer = null
        this._highlightName = null
      }
    },
    hitTest(mx, my) {
      if (!this._nodes) return null
      let best = null
      let bestD = Infinity
      this._nodes.forEach(n => {
        const d = Math.hypot(mx - n.x, my - n.y)
        if (d <= n.r + 14 && d < bestD) { best = n; bestD = d }
      })
      return best
    },
    // 屏幕坐标 → Canvas 设计空间坐标（舞台 7680 坐标系，已抵消 transform:scale 与 dpr 影响）
    // 否则在缩放屏（s≠1）上点击/悬浮坐标会被错误缩放，导致圆点命中错位、点不进详情
    toLocal(e) {
      const el = this.$refs.heatCanvas
      const rect = el.getBoundingClientRect()
      const kx = (this._cw || rect.width) / rect.width
      const ky = (this._ch || rect.height) / rect.height
      return { mx: (e.clientX - rect.left) * kx, my: (e.clientY - rect.top) * ky }
    },
    attachCanvasEvents() {
      const el = this.$refs.heatCanvas
      if (!el || el._heatBound) return
      el._heatBound = true
      this._onMove = (e) => {
        const { mx, my } = this.toLocal(e)
        const hit = this.hitTest(mx, my)
        el.style.cursor = hit ? 'pointer' : 'default'
        if (hit) {
          const html = hit.id === 'hub'
            ? this.$C('downstreamHeat', 'hubTooltip', '康巴圣德（酪蛋白酸钠生产商）· 热力枢纽')
            : `<b>${hit.province} · ${getCompanyById(hit.id).name}</b>` +
              (hit.isListed && hit.stock ? `<br/><span style="color:#FFC857">📈 上市公司 · ${hit.stock.market} ${hit.stock.code}（${hit.stock.exchange}${hit.stock.board}）</span>` : '') +
              `<br/>热力值 <b style="color:#FFC857">${hit.heat}</b>（公开信息「酪蛋白」字样 ×${hit.mentions}）<br/>${hit.note}<br/><span style="color:#8fb4e6">点击节点查看企业详情</span>`
          this.tip = { show: true, x: mx + 44, y: my + 36, html }
        } else {
          this.hideTip()
        }
      }
      this._onLeave = () => this.hideTip()
      this._onClick = (e) => {
        const { mx, my } = this.toLocal(e)
        const hit = this.hitTest(mx, my)
        if (hit && hit.id !== 'hub') this.goCompany(hit.id)
      }
      el.addEventListener('mousemove', this._onMove)
      el.addEventListener('mouseleave', this._onLeave)
      el.addEventListener('click', this._onClick)
    },
    detachCanvasEvents() {
      const el = this.$refs.heatCanvas
      if (!el) return
      if (this._onMove) el.removeEventListener('mousemove', this._onMove)
      if (this._onLeave) el.removeEventListener('mouseleave', this._onLeave)
      if (this._onClick) el.removeEventListener('click', this._onClick)
    },
    hideTip() {
      if (this.tip.show) this.tip = Object.assign({}, this.tip, { show: false })
    },
  },
}
</script>

<style lang="less" scoped>
.dh-page {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--page-bg);
  color: var(--text-main);
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}
.dh-back {
  display: inline-flex;
  align-items: center;
  gap: 20px;
  padding: 24px 48px;
  font-size: 40px;
  color: #eaf3ff;
  background: rgba(10, 40, 84, 0.72);
  border: 1px solid rgba(79, 163, 255, 0.4);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.dh-back:hover { background: rgba(31, 122, 224, 0.85); }
.dh-back-arrow { font-size: 48px; line-height: 1; }

.dh-stage {
  width: 7680px;
  height: 2160px;
  min-height: 2160px;
  padding: 90px 120px 56px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* 闪烁星空斑点层：铺满舞台、最底层、不挡交互 */
.dh-stars {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}
.dh-stars i {
  position: absolute;
  display: block;
  border-radius: 50%;
  animation-name: dh-twinkle;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
.dh-stars .s-white { background: #fff; box-shadow: 0 0 8px rgba(255,255,255,0.85); }
.dh-stars .s-cyan  { background: #31FFFF; box-shadow: 0 0 10px rgba(49,255,255,0.9); }
.dh-stars .s-gold  { background: #FFC857; box-shadow: 0 0 10px rgba(255,200,87,0.9); }
.dh-stars .s-big    { box-shadow: 0 0 18px rgba(255,255,255,0.95), 0 0 30px rgba(49,255,255,0.5); }
@keyframes dh-twinkle {
  0%, 100% { opacity: 0.08; transform: scale(0.55); }
  50%      { opacity: 1;    transform: scale(1.18); }
}
/* 内容压在星空之上 */
.dh-header, .dh-body, .dh-footer { position: relative; z-index: 1; }

/* 页头 */
.dh-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 44px 76px;
  margin-bottom: 52px;
  background: linear-gradient(90deg, rgba(13,42,84,0.7), rgba(10,30,64,0.2));
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  flex-shrink: 0;
}
.dh-title {
  margin: 0 0 24px;
  font-size: 84px;
  font-weight: 800;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 30px;
  text-shadow: 0 4px 18px rgba(0, 180, 255, 0.35);
}
.dh-flame {
  width: 66px;
  height: 78px;
  border-radius: 50% 50% 50% 50% / 62% 62% 38% 38%;
  background: radial-gradient(circle at 50% 72%, #fff3c4 0%, #FFC857 42%, #FF7A3C 78%, rgba(255,122,60,0) 100%);
  box-shadow: 0 0 24px rgba(255, 170, 60, 0.8);
  animation: dh-flame-breathe 1.6s ease-in-out infinite;
}
@keyframes dh-flame-breathe {
  0%, 100% { transform: scale(1); box-shadow: 0 0 18px rgba(255,170,60,0.65); }
  50% { transform: scale(1.12); box-shadow: 0 0 32px rgba(255,200,87,0.95); }
}
.dh-chips { display: flex; gap: 28px; flex-wrap: wrap; }
.dh-chip {
  font-size: 40px;
  color: var(--text-sub);
  padding: 18px 40px;
  border: 1px solid rgba(120, 170, 240, 0.3);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.04);
}
.dh-chip-key {
  color: #04122e;
  background: var(--accent2);
  border-color: var(--accent2);
  font-weight: 700;
  animation: dh-key-pulse 2s ease-in-out infinite;
}
.dh-chip-list {
  color: #04122e;
  background: #FF6B6B;
  border-color: #FF6B6B;
  font-weight: 700;
  animation: dh-list-pulse 2s ease-in-out infinite;
}
@keyframes dh-list-pulse {
  0%, 100% { box-shadow: 0 0 10px rgba(255,107,107,0.5); }
  50% { box-shadow: 0 0 26px rgba(255,107,107,0.95); }
}
@keyframes dh-key-pulse {
  0%, 100% { box-shadow: 0 0 10px rgba(255,200,87,0.5); }
  50% { box-shadow: 0 0 26px rgba(255,200,87,0.95); }
}

/* 主体两栏 */
.dh-body {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 64px;
}
/* 左：热力榜 */
.dh-board {
  width: 1800px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 48px 56px;
  min-height: 0;
}
.dh-board-head {
  font-size: 60px;
  font-weight: 800;
  color: var(--accent);
  display: flex;
  align-items: center;
  gap: 22px;
  padding-bottom: 32px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}
.dh-board-pin {
  width: 16px;
  height: 48px;
  border-radius: 6px;
  background: var(--accent2);
  box-shadow: 0 0 12px var(--accent2);
}
.dh-rank {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 26px;
}
.dh-row {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 14px 32px; /* 2221 纵向 2160 预算内收紧行高，避免 10 行榜单 + 页脚溢出舞台 */
  border-radius: 14px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}
.dh-row:hover { background: rgba(47,127,214,0.18); border-color: rgba(79,163,255,0.4); }
.dh-rank-no {
  width: 84px;
  height: 84px;
  line-height: 84px;
  text-align: center;
  border-radius: 50%;
  font-size: 40px;
  font-weight: 800;
  color: #cfe2ff;
  background: rgba(120,170,240,0.16);
  border: 1px solid rgba(120,170,240,0.3);
  flex-shrink: 0;
}
.rank-1 { background: rgba(255,200,87,0.1); border-color: rgba(255,200,87,0.55); animation: dh-top1-glow 2s ease-in-out infinite; }
.rank-1 .dh-rank-no { color: #04122e; background: var(--accent2); border-color: var(--accent2); box-shadow: 0 0 16px rgba(255,200,87,0.8); }
.rank-2 .dh-rank-no, .rank-3 .dh-rank-no { color: #eaf3ff; background: rgba(49,255,255,0.14); border-color: rgba(49,255,255,0.45); }
@keyframes dh-top1-glow {
  0%, 100% { box-shadow: 0 0 8px rgba(255,200,87,0.25); }
  50% { box-shadow: 0 0 26px rgba(255,200,87,0.6); }
}
.dh-row-main { flex: 1; min-width: 0; }
.dh-row-line1 {
  display: flex;
  align-items: baseline;
  gap: 28px;
  margin-bottom: 18px;
}
.dh-row-name { font-size: 44px; font-weight: 700; color: #fff; white-space: nowrap; }
.dh-row-listed {
  font-size: 30px;
  font-weight: 700;
  color: #04122e;
  background: #FF6B6B;
  border-radius: 18px;
  padding: 4px 18px;
  white-space: nowrap;
  flex-shrink: 0;
}
.dh-row-prov { font-size: 36px; color: var(--text-sub); }
.dh-row-heat { margin-left: auto; font-size: 46px; font-weight: 800; color: var(--accent2); font-family: 'DIN', 'Arial', sans-serif; }
.dh-bar { height: 20px; border-radius: 8px; background: rgba(120,170,240,0.14); overflow: hidden; }
.dh-bar-fill {
  display: block;
  height: 100%;
  border-radius: 8px;
  background: linear-gradient(90deg, #1a7cff 0%, #31FFFF 70%, #FFC857 100%);
  transition: width 1.2s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.rank-1 .dh-bar-fill { background: linear-gradient(90deg, #FF7A3C 0%, #FFC857 100%); }

/* 右：游走网络图（原生 canvas 自绘） */
.dh-chart-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: radial-gradient(circle at 50% 42%, rgba(16, 52, 108, 0.42), rgba(6, 20, 48, 0.2));
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
  position: relative;
}
.dh-canvas {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: block;
  cursor: default;
}
.dh-tooltip {
  position: absolute;
  z-index: 5;
  max-width: 1000px;
  padding: 26px 38px;
  font-size: 34px;
  line-height: 1.6;
  color: #fff;
  background: rgba(6, 22, 52, 0.94);
  border: 1px solid rgba(79, 163, 255, 0.5);
  border-radius: 12px;
  pointer-events: none;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
}
.dh-chart-tip {
  flex-shrink: 0;
  text-align: center;
  padding: 24px 0 30px;
  font-size: 34px;
  color: #7fa3cc;
}

/* 页脚 */
.dh-footer {
  flex-shrink: 0;
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 36px;
  padding: 28px 56px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 32px;
  color: #7fa3cc;
}
.dh-footer-logo { color: var(--accent); font-weight: 700; font-size: 36px; }
</style>
